/**
 * Release tour — Consent Management (PROD-1051, document-centric rework).
 *
 * The standalone `/consent` surface was retired — the template builder moved to
 * Studio › Consent Forms, and patient signing now lives in the chart's Docs → Forms
 * surface (see `src/App.tsx`'s `/consent` redirect + commit #573). This tour targets
 * the CURRENT location: real `data-tour` anchors on `studio-launchpad-page.tsx`'s tab
 * bar and `consent-template-library.tsx` — not the old tab-based workspace.
 *
 * Signing-method copy is deliberately vendor-blind ("your pad", never a brand name) —
 * matches the real UI in `consent-fill-sign-popup.tsx`, whose live toggle reads "On
 * screen" / "On pad" and never surfaces a vendor. Today that toggle only detects a
 * Topaz unit via SigWeb, but the underlying `SignatureCaptureProvider` interface
 * (`src/consent/signature/`) is written to the same vendor-blind contract for
 * Signotec, Wacom, and UAE PASS QES — the UI is future-proofed even where only one
 * pad is wired today, so the copy should never name the one that happens to be first.
 *
 * Mount at the top of `ConsentFormsPage`'s library screen (its landing view):
 *   const tour = useReleaseTour(CONSENT_MANAGEMENT_TOUR, { userRole: user.role });
 *   return <>{...library}<ReleaseTour {...tour} /></>;
 */
import type { ReleaseTourConfig } from '../release-tour-types';
import { registerReleaseTour } from '../release-tour-registry';

export const CONSENT_MANAGEMENT_TOUR: ReleaseTourConfig = {
  id: 'consent-management-v3',
  name: 'Consent Management',
  enabled: true,
  steps: [
    {
      target: '[data-tour="studio-tab-consent-forms"]',
      title: 'Introducing Consent Forms',
      content:
        'Consent moves to Studio — built on Scrips’ new engine, and the first of several workflows getting a from-scratch rebuild this year.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="consent-library-header"]',
      title: 'Your template library',
      content:
        'Every consent form your clinic offers for signing, in one list — drafts, published, and retired.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="consent-new-template"]',
      title: 'Build a new template',
      content: 'Upload a clinic PDF or start from scratch, then publish it once it is ready to sign.',
      placement: 'left',
    },
    {
      target: '[data-tour="consent-template-row"]',
      title: 'Signing works your way',
      content:
        'Patients sign on screen, or on whatever pad is already at your front desk — same form, same file, no new hardware to buy.',
      placement: 'top',
    },
    {
      target: '[data-tour="consent-library-table"]',
      title: 'This is just the beginning',
      content:
        'Every signature is filed and auditable the moment it’s captured — the foundation for what’s coming next: AI that pre-fills forms and flags what still needs a signature, before you even ask. This tour won’t show again.',
      placement: 'top',
    },
  ],
};

registerReleaseTour(CONSENT_MANAGEMENT_TOUR);
